/**
 * HÀNG ĐỢI TẢI VIDEO LÊN KÊNH (06/10/2026, chủ dự án: «upload video lên kênh và tự động chỉnh sửa tiêu đề,
 * mô tả, tag theo mẫu»).
 *
 * Nằm ngoài màn «Đăng video» như hàng đợi Kho nhạc: một video 6–18 GB lên mất cả giờ, người dùng sang mục khác
 * làm việc thì vẫn chạy, quay lại vẫn thấy tiến độ. Bản đầu gửi MỖI LÚC MỘT video; từ 09/10/2026 (chủ dự án: «Phần
 * upload có thể đẩy nhanh hơn») chọn được nhiều video cùng lúc, mặc định 2: một luồng tới Google thường không dùng
 * hết đường lên, mấy luồng song song cộng lại nhanh hơn; mạng đã kín thì để 1. Trần 3 làn tới 16/10/2026, nới lên 6
 * (chủ dự án: «Tăng số lượng lượt tải lên nhiều hơn» — đang chạy kịch 3 làn); hơn nữa không nhanh thêm vì cổng mạng
 * văn phòng 100 Mbps chia đều cho các làn. Đường đi của một video:
 *   1. xin máy chủ mở phiên (tiêu đề · mô tả · tag · giờ hẹn đi cùng) → địa chỉ phiên của Google;
 *   2. đẩy file thẳng lên Google (`upload-runner.ts`), tự gửi tiếp khi đứt mạng;
 *   3. báo máy chủ xong: ghi nhật ký, thêm vào danh sách phát; rồi gửi ảnh bìa (nếu có).
 * File (video, ảnh bìa) giữ ngoài store — store chỉ giữ thứ cần vẽ.
 */
import type { TemplateSource, VideoMetadata, YoutubePrivacy } from '@halo/shared';
import { create } from 'zustand';
import { errorMessage, isAbortError } from '@/api/client';
import { youtubePublishApi } from '@/api/endpoints';
import { queryClient } from '@/app/query-client';
import { isQuotaExhaustedMessage } from '@/domain/video-publish';
import type { UploadKind } from '@/domain/video-upload';
import { registerSessionReset } from '@/stores/session-reset';
import { useThumbnailMarks } from './thumbnail-marks';
import { moveToUploaded, toolFolderStoreOf } from './tool-folder';
import { UploadSessionLostError, uploadToYoutube } from './upload-runner';

export type VideoUploadStatus = 'ready' | 'uploading' | 'finishing' | 'done' | 'failed';

export interface VideoUploadItem {
  id: number;
  channelId: string;
  /** Video dài (tool ToolNhacViet) hay video ngắn (tool MV, 27/10/2026) — mỗi loại một tab, một thư mục tool. */
  kind: UploadKind;
  /** Tên bộ (thư mục hoặc tên file không đuôi). */
  name: string;
  fileName: string;
  size: number;
  hasSeo: boolean;
  hasThumb: boolean;
  /** Nội dung gốc (từ seo.txt / tên file) để áp lại mẫu khi đổi ngày công khai. */
  source: TemplateSource;
  meta: VideoMetadata;
  /** Người dùng đã sửa tay tiêu đề · mô tả · tag — không áp lại mẫu đè lên nữa. */
  edited: boolean;
  /** Giờ công khai (ISO); `null` = không hẹn, hiển thị theo `privacy`. */
  publishAt: string | null;
  /** Hiển thị khi không hẹn giờ (không công khai / riêng tư); hẹn giờ thì riêng tư tới giờ ấy. */
  privacy: YoutubePrivacy;
  playlistId: string;
  /**
   * Thư mục của bộ, đường dẫn tương đối so với chỗ kéo vào («KÊNH 0007», «UPLOAD/KÊNH 0007») — để chuyển vào «Đã
   * Upload» khi lên xong (`moveToUploaded` tự tìm dưới thư mục tool đã chọn); '' = file rời.
   */
  folderName: string;
  /** Chuyển thư mục vào «Đã Upload» (07/10/2026): '' chưa / không thể, chờ tới lượt, đang chuyển, xong, hỏng. */
  moved: '' | 'queued' | 'moving' | 'done' | 'failed';
  /** Tên thư mục ở chỗ mới, hoặc lý do hỏng. */
  movedNote: string;
  /** Phải chép (trình duyệt không dời được): byte đã chép / tổng byte — vẽ tiến độ; 0 khi dời tức thì. */
  moveDone: number;
  moveTotal: number;
  /** Đã chuyển bằng cách chép chứ không dời tức thì — chậm, đáng biết. */
  moveCopied: boolean;
  status: VideoUploadStatus;
  /** Byte Google đã nhận (vẽ thanh tiến độ). */
  sent: number;
  /** Tốc độ gần đây, byte/giây. */
  rate: number;
  /** Phiên tải lên đang dở — giữ lại để gửi tiếp. */
  uploadUrl: string;
  videoId: string;
  error: string;
  warning: string;
}

interface VideoUploadState {
  items: VideoUploadItem[];
  /** Đang chạy hàng đợi (bấm «Tải lên»); «Dừng» thì tắt, video đang gửi dở giữ lại phiên. */
  running: boolean;
  /** Số video gửi CÙNG LÚC (09/10/2026, chủ dự án: «Phần upload có thể đẩy nhanh hơn»): 1–3, nhớ theo máy. */
  lanes: number;
  setLanes: (lanes: number) => void;
  add: (items: NewUploadItem[]) => void;
  update: (id: number, patch: Partial<VideoUploadItem>) => void;
  remove: (id: number) => void;
  start: () => void;
  stop: () => void;
  clearDone: (channelId: string, kind?: UploadKind) => void;
  reset: () => void;
}

export interface NewUploadItem {
  channelId: string;
  /** Không ghi = video dài. */
  kind?: UploadKind;
  name: string;
  video: File;
  thumb: File | null;
  hasSeo: boolean;
  source: TemplateSource;
  meta: VideoMetadata;
  publishAt: string | null;
  privacy: YoutubePrivacy;
  playlistId: string;
  folderName: string;
  /** Điều đáng biết ngay từ lúc xếp vào (ảnh bìa quá cỡ nên bỏ…). */
  warning?: string;
}

const files = new Map<number, { video: File; thumb: File | null }>();
/** Video đang gửi → nút huỷ của nó (gửi nhiều video cùng lúc). */
const inFlight = new Map<number, AbortController>();
const abortAll = () => inFlight.forEach((controller) => controller.abort());
/** Số «làn» gửi đang chạy. */
let workers = 0;
/** Video đã được tự mở lại phiên một lần (phiên Google mất) — lần sau thì báo lỗi. */
const reopened = new Set<number>();
let nextId = 1;
let generation = 0;

export const UPLOAD_LANES_MAX = 6;
export const UPLOAD_LANES_DEFAULT = 2;
const LANES_KEY = 'halo-upload-lanes';
function storedLanes(): number {
  try {
    const value = Number(localStorage.getItem(LANES_KEY));
    return value >= 1 && value <= UPLOAD_LANES_MAX ? Math.floor(value) : UPLOAD_LANES_DEFAULT;
  } catch {
    return UPLOAD_LANES_DEFAULT;
  }
}

const warnBeforeLeaving = (event: BeforeUnloadEvent) => event.preventDefault();
function guardLeaving(active: boolean): void {
  if (typeof window === 'undefined') return;
  if (active) window.addEventListener('beforeunload', warnBeforeLeaving);
  else window.removeEventListener('beforeunload', warnBeforeLeaving);
}

export const useVideoUploadStore = create<VideoUploadState>((set, get) => ({
  items: [],
  running: false,
  lanes: storedLanes(),

  setLanes(lanes) {
    const value = Math.min(UPLOAD_LANES_MAX, Math.max(1, Math.floor(lanes)));
    set({ lanes: value });
    try {
      localStorage.setItem(LANES_KEY, String(value));
    } catch {
      // không lưu được thì lần sau về mặc định
    }
    if (get().running) drain();
  },

  add(newItems) {
    const added = newItems.map((item): VideoUploadItem => {
      const id = nextId++;
      files.set(id, { video: item.video, thumb: item.thumb });
      return {
        id,
        channelId: item.channelId,
        kind: item.kind ?? 'long',
        name: item.name,
        fileName: item.video.name,
        size: item.video.size,
        hasSeo: item.hasSeo,
        hasThumb: Boolean(item.thumb),
        source: item.source,
        meta: item.meta,
        edited: false,
        publishAt: item.publishAt,
        privacy: item.privacy,
        playlistId: item.playlistId,
        folderName: item.folderName,
        moved: '',
        movedNote: '',
        moveDone: 0,
        moveTotal: 0,
        moveCopied: false,
        status: 'ready',
        sent: 0,
        rate: 0,
        uploadUrl: '',
        videoId: '',
        error: '',
        warning: item.warning ?? '',
      };
    });
    set((state) => ({ items: [...state.items, ...added] }));
  },

  update: (id, patch) =>
    set((state) => ({
      items: state.items.map((item) => (item.id === id ? { ...item, ...patch } : item)),
    })),

  remove(id) {
    const item = get().items.find((entry) => entry.id === id);
    if (item && (item.status === 'uploading' || item.status === 'finishing')) {
      inFlight.get(id)?.abort();
    }
    files.delete(id);
    set((state) => ({ items: state.items.filter((entry) => entry.id !== id) }));
  },

  start() {
    set((state) => ({
      running: true,
      items: state.items.map((item) =>
        item.status === 'failed' ? { ...item, status: 'ready', error: '' } : item,
      ),
    }));
    guardLeaving(true);
    drain();
  },

  stop() {
    set({ running: false });
    abortAll();
  },

  clearDone: (channelId, kind) =>
    set((state) => ({
      items: state.items.filter((item) => {
        const gone =
          item.channelId === channelId && item.status === 'done' && (!kind || item.kind === kind);
        if (gone) files.delete(item.id);
        return !gone;
      }),
    })),

  reset() {
    generation += 1;
    abortAll();
    files.clear();
    reopened.clear();
    guardLeaving(false);
    set({ items: [], running: false });
  },
}));

/** Video chờ tới lượt: theo thứ tự xếp vào, kênh nào cũng được. */
const nextReady = () =>
  useVideoUploadStore.getState().items.find((item) => item.status === 'ready');

/**
 * Mở thêm «làn» gửi cho tới đủ `lanes`; mỗi làn lấy video chờ kế tiếp, gửi xong lấy tiếp. `runItem` đánh dấu video
 * «đang gửi» ngay trước lần chờ đầu tiên, nên hai làn không bao giờ lấy trùng một video.
 */
function drain(): void {
  const startedIn = generation;
  while (workers < useVideoUploadStore.getState().lanes && nextReady()) {
    workers += 1;
    void work(startedIn);
  }
  if (workers === 0) settle(startedIn);
}

async function work(startedIn: number): Promise<void> {
  try {
    for (;;) {
      const state = useVideoUploadStore.getState();
      // Giảm số video cùng lúc giữa chừng: làn thừa nghỉ sau khi gửi xong video đang gửi.
      if (!state.running || generation !== startedIn || workers > state.lanes) return;
      const item = nextReady();
      if (!item) return;
      await runItem(item, startedIn);
    }
  } finally {
    workers -= 1;
    if (workers === 0) settle(startedIn);
  }
}

/** Làn cuối cùng vừa nghỉ: hết việc thì tắt; còn việc mà vẫn đang chạy (bấm lại «Tải lên») thì mở làn mới. */
function settle(startedIn: number): void {
  const state = useVideoUploadStore.getState();
  const pending = state.items.some((item) => item.status === 'ready');
  if (!pending) useVideoUploadStore.setState({ running: false });
  if (!pending || !state.running) guardLeaving(false);
  else if (generation === startedIn) queueMicrotask(drain);
  else drain();
}

async function runItem(item: VideoUploadItem, startedIn: number): Promise<void> {
  const { update } = useVideoUploadStore.getState();
  const stored = files.get(item.id);
  if (!stored) {
    update(item.id, { status: 'failed', error: 'Mất file — thêm lại video này' });
    return;
  }
  const controller = new AbortController();
  inFlight.set(item.id, controller);
  const signal = controller.signal;
  update(item.id, { status: 'uploading', error: '', warning: '' });

  try {
    let uploadUrl = item.uploadUrl;
    const resume = Boolean(uploadUrl);
    if (!uploadUrl) {
      const started = await youtubePublishApi.startUpload(item.channelId, {
        fileName: stored.video.name,
        fileSize: stored.video.size,
        mimeType: stored.video.type || 'video/*',
        title: item.meta.title,
        description: item.meta.description,
        tags: item.meta.tags,
        publishAt: item.publishAt,
        privacy: item.privacy,
      });
      if (generation !== startedIn) return;
      uploadUrl = started.uploadUrl;
      update(item.id, { uploadUrl });
    }

    // Tốc độ: trung bình trượt trên ~3 giây gần nhất; vẽ lại tối đa 2 lần mỗi giây.
    let lastAt = performance.now();
    let lastSent = item.sent;
    let rate = 0;
    const videoId = await uploadToYoutube(uploadUrl, stored.video, {
      signal,
      resume,
      onProgress: ({ sent }) => {
        const now = performance.now();
        const elapsed = (now - lastAt) / 1000;
        if (elapsed < 0.5 && sent < stored.video.size) return;
        const instant = elapsed > 0 ? Math.max(0, sent - lastSent) / elapsed : 0;
        rate = rate ? rate * 0.7 + instant * 0.3 : instant;
        lastAt = now;
        lastSent = sent;
        update(item.id, { sent, rate });
      },
    });
    if (generation !== startedIn) return;
    update(item.id, { status: 'finishing', videoId, sent: stored.video.size, rate: 0 });

    const finished = await youtubePublishApi.finishUpload(item.channelId, {
      videoId,
      ...(item.playlistId ? { playlistId: item.playlistId } : {}),
    });
    if (generation !== startedIn) return;
    const warnings = finished.warning ? [finished.warning] : [];
    if (stored.thumb) {
      try {
        await youtubePublishApi.thumbnail(item.channelId, videoId, stored.thumb);
        // Bảng Video vẽ ảnh với địa chỉ có `v=` để không hiện ảnh xám cũ trong bộ đệm (17/10/2026).
        useThumbnailMarks.getState().mark(videoId);
      } catch (error) {
        warnings.push(`Chưa đặt được ảnh bìa: ${errorMessage(error)}`);
      }
    }
    if (generation !== startedIn) return;
    update(item.id, { status: 'done', warning: warnings.join(' · ') });
    files.delete(item.id);
    void queryClient.invalidateQueries({ queryKey: ['youtube-publish', item.channelId] });
    void moveAfterUpload(item.id);
  } catch (error) {
    if (generation !== startedIn) return;
    if (isAbortError(error)) {
      // Bấm «Dừng» (hoặc bỏ video): để sẵn ở trạng thái chờ, phiên giữ lại để gửi tiếp.
      const still = useVideoUploadStore.getState().items.some((entry) => entry.id === item.id);
      if (still) update(item.id, { status: 'ready', rate: 0 });
      return;
    }
    // Phiên Google mất (404 / 410): tự mở phiên mới và gửi lại từ đầu MỘT lần, khỏi bắt người dùng bấm lại.
    if (error instanceof UploadSessionLostError && !reopened.has(item.id)) {
      reopened.add(item.id);
      update(item.id, { status: 'ready', uploadUrl: '', sent: 0, rate: 0, error: '' });
      return;
    }
    const text = errorMessage(error);
    // Mọi client đều hết lượt / hết hạn mức: dừng hàng đợi, video sau gửi cũng hỏng.
    if (isQuotaExhaustedMessage(text)) useVideoUploadStore.setState({ running: false });
    update(item.id, {
      status: 'failed',
      rate: 0,
      error: text,
      // Phiên hết hạn thì lần sau phải mở phiên mới; lỗi khác giữ phiên để gửi tiếp chỗ dở.
      ...(error instanceof UploadSessionLostError ? { uploadUrl: '', sent: 0 } : {}),
    });
  } finally {
    inFlight.delete(item.id);
  }
}

/**
 * Chuyển thư mục tool của video đã lên vào «Đã Upload» (07/10/2026, chủ dự án: «Những folder đã upload lên kênh
 * bạn có thể bốc sang Folder đã Upload»). Chỉ khi đã cho quyền ghi thư mục tool và bộ có thư mục; đưa xong mới
 * xoá chỗ cũ. Hỏng thì ghi lý do để bấm «Thử lại», không ảnh hưởng video đã lên. Các lần chuyển NỐI ĐUÔI NHAU
 * (16/10/2026, chủ dự án: «bốc video đã upload đang bị đơ hoặc copy lâu»): nhiều video xong gần nhau mà chép đồng
 * thời trên một ổ cứng thì chậm hơn chép lần lượt và làm trang đơ — xếp hàng, mỗi lúc một thư mục.
 */
let moveChain: Promise<void> = Promise.resolve();
function enqueueMove(task: () => Promise<void>): Promise<void> {
  const run = moveChain.then(task, task);
  moveChain = run.catch(() => undefined);
  return run;
}

export async function moveAfterUpload(id: number): Promise<void> {
  const { update } = useVideoUploadStore.getState();
  const item = useVideoUploadStore.getState().items.find((entry) => entry.id === id);
  if (
    !item ||
    item.status !== 'done' ||
    !item.folderName ||
    !toolFolderStoreOf(item.kind).getState().root ||
    item.moved === 'queued' ||
    item.moved === 'moving'
  )
    return;
  update(id, { moved: 'queued', movedNote: '', moveDone: 0, moveTotal: 0, moveCopied: false });
  await enqueueMove(async () => {
    const current = useVideoUploadStore.getState().items.find((entry) => entry.id === id);
    const root = toolFolderStoreOf(item.kind).getState().root;
    // Video đã bị bỏ khỏi hàng đợi, hay thư mục tool bị bỏ chọn trong lúc chờ → thôi.
    if (!current || current.moved !== 'queued') return;
    if (!root) {
      update(id, { moved: '' });
      return;
    }
    update(id, { moved: 'moving' });
    try {
      const result = await moveToUploaded(root, current.folderName, {
        onProgress: (done, total) => update(id, { moveDone: done, moveTotal: total }),
      });
      update(id, {
        moved: 'done',
        movedNote: result.name,
        moveCopied: result.copied,
        moveDone: 0,
        moveTotal: 0,
      });
    } catch (error) {
      update(id, { moved: 'failed', movedNote: errorMessage(error), moveDone: 0, moveTotal: 0 });
    }
  });
}

/** Mọi video đã lên của kênh mà thư mục còn chưa chuyển — bấm một nút sau khi cấp quyền thư mục tool. */
export async function moveAllAfterUpload(channelId: string, kind?: UploadKind): Promise<number> {
  const pending = useVideoUploadStore
    .getState()
    .items.filter(
      (item) =>
        item.channelId === channelId &&
        (!kind || item.kind === kind) &&
        item.status === 'done' &&
        item.folderName &&
        item.moved !== 'done' &&
        item.moved !== 'queued' &&
        item.moved !== 'moving',
    );
  // Xếp hàng cả loạt ngay (mỗi video hiện «chờ chuyển»), rồi đợi hàng chạy xong.
  await Promise.all(pending.map((item) => moveAfterUpload(item.id)));
  return pending.length;
}

registerSessionReset(() => useVideoUploadStore.getState().reset());
